import type { DlqMessageSummary, DlqMessagesResponse, QueueOverview } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumbs } from '@/components/breadcrumbs';
import { RedriveDialog } from '@/components/jobs/redrive-dialog';
import { RunbookChat } from '@/components/runbook-chat';
import { DataTableSkeleton, EmptyState, ErrorBanner, LoadingCard } from '@/components/ui/state-views';
import { getJobDlq, getJobQueues } from '@/config/api-helpers';
import { DEV_CONSOLE_AXIOM_QUERY_BASE_URL, DEV_CONSOLE_AXIOM_WORKER_LOGS_DATASET } from '@/config/devConsole';
import { recordVisit } from '@/hooks/useVisitHistory';
import { formatTimestamp, truncate } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_RUNBOOK_WIDTH = 480;
const MIN_RUNBOOK_WIDTH = 320;
const MIN_MAIN_WIDTH = 480;

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function formatJsonBody(body: string): string {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

function buildAxiomUrl(message: DlqMessageSummary, canonicalJob: string): string | null {
  const filters: string[] = [];
  if (canonicalJob) filters.push(`['context.canonicalJob'] contains "${canonicalJob}"`);
  if (message.jobName) filters.push(`['context.job'] == "${message.jobName}"`);
  if (message.traceId) {
    filters.push(
      `(['context.traceId'] == "${message.traceId}" or ['context.upstreamTraceId'] == "${message.traceId}")`
    );
  }
  if (filters.length === 0) return null;

  const aplQuery = `['${DEV_CONSOLE_AXIOM_WORKER_LOGS_DATASET}']\n| where ${filters.join(' and ')}`;
  const initForm: Record<string, string> = { apl: aplQuery };

  return `${DEV_CONSOLE_AXIOM_QUERY_BASE_URL}?initForm=${encodeURIComponent(JSON.stringify(initForm))}`;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useCanonicalJobDlq(canonicalJob?: string) {
  const [dlqMessages, setDlqMessages] = useState<DlqMessagesResponse['messages']>([]);
  const [dlqLoading, setDlqLoading] = useState(false);
  const [dlqUrl, setDlqUrl] = useState('');
  const [smartRedriveInfo, setSmartRedriveInfo] = useState<DlqMessagesResponse['smartRedriveInfo']>(null);

  const refreshDlq = useCallback(async () => {
    if (!canonicalJob) return;

    setDlqLoading(true);
    try {
      const data = await getJobDlq(canonicalJob);
      setDlqMessages(data.messages);
      setDlqUrl(data.dlqUrl);
      setSmartRedriveInfo(data.smartRedriveInfo);
    } catch {
      setDlqMessages([]);
      setDlqUrl('');
      setSmartRedriveInfo(null);
    } finally {
      setDlqLoading(false);
    }
  }, [canonicalJob]);

  useEffect(() => {
    refreshDlq();
  }, [refreshDlq]);

  return { dlqLoading, dlqMessages, dlqUrl, refreshDlq, smartRedriveInfo };
}

function useDlqSelection(messages: DlqMessageSummary[]) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = useCallback((messageId: string) => {
    setSelectedIds((currentSelectedIds) => {
      const nextSelectedIds = new Set(currentSelectedIds);

      if (nextSelectedIds.has(messageId)) {
        nextSelectedIds.delete(messageId);
      } else {
        nextSelectedIds.add(messageId);
      }

      return nextSelectedIds;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((currentSelectedIds) => {
      if (currentSelectedIds.size === messages.length) {
        return new Set();
      }

      return new Set(messages.map((message) => message.messageId));
    });
  }, [messages]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const toggleExpanded = useCallback((messageId: string) => {
    setExpandedId((currentExpandedId) => (currentExpandedId === messageId ? null : messageId));
  }, []);

  const selectedMessages = useMemo(
    () => messages.filter((message) => selectedIds.has(message.messageId)),
    [messages, selectedIds]
  );

  return {
    clearSelection,
    expandedId,
    hasSelection: selectedIds.size > 0,
    selectedIds,
    selectedMessages,
    toggleExpanded,
    toggleSelect,
    toggleSelectAll,
  };
}

function useJobNameFilters(dlqMessages: DlqMessageSummary[]) {
  const [jobNameFilters, setJobNameFilters] = useState<Set<string>>(new Set());

  const dlqByJobName = useMemo(() => {
    const counts = new Map<string, number>();

    for (const message of dlqMessages) {
      const name = message.jobName || '(unknown)';
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }

    return [...counts.entries()].sort((left, right) => right[1] - left[1]);
  }, [dlqMessages]);

  const filteredDlqMessages = useMemo(() => {
    if (jobNameFilters.size === 0) {
      return dlqMessages;
    }

    return dlqMessages.filter((message) => jobNameFilters.has(message.jobName));
  }, [dlqMessages, jobNameFilters]);

  const toggleJobNameFilter = useCallback((name: string) => {
    setJobNameFilters((currentFilters) => {
      const nextFilters = new Set(currentFilters);

      if (nextFilters.has(name)) {
        nextFilters.delete(name);
      } else {
        nextFilters.add(name);
      }

      return nextFilters;
    });
  }, []);

  const clearJobNameFilters = useCallback(() => {
    setJobNameFilters(new Set());
  }, []);

  return { clearJobNameFilters, dlqByJobName, filteredDlqMessages, jobNameFilters, toggleJobNameFilter };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CanonicalJobPage() {
  const { canonicalJob } = useParams();
  const {
    data: queues,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['job-queues'],
    queryFn: getJobQueues,
  });

  const [dialogMode, setDialogMode] = useState<'redrive' | 'discard' | null>(null);
  const [showRunbook, setShowRunbook] = useState(false);
  const [runbookWidth, setRunbookWidth] = useState(DEFAULT_RUNBOOK_WIDTH);

  const queue = queues?.find((q) => q.canonicalJob === canonicalJob);

  useEffect(() => {
    if (canonicalJob) {
      recordVisit(`/jobs/${encodeURIComponent(canonicalJob)}`, canonicalJob, 'queue');
    }
  }, [canonicalJob]);

  function handleRefresh() {
    refetch();
  }

  return (
    <div className='space-y-6' style={showRunbook ? { marginRight: runbookWidth } : undefined}>
      <Breadcrumbs items={[{ label: 'Job Queues', to: '/jobs' }, { label: canonicalJob ?? '' }]} />

      {error ? (
        <ErrorBanner message={error?.message ?? ''} />
      ) : isLoading ? (
        <LoadingCard message='Loading…' />
      ) : !queue ? (
        <EmptyState message='Queue not found.' />
      ) : (
        <CanonicalJobContent
          dialogMode={dialogMode}
          onDialogModeChange={setDialogMode}
          onOpenRunbook={() => setShowRunbook(true)}
          onRefresh={handleRefresh}
          queue={queue}
        />
      )}

      {showRunbook && (
        <RunbookChat
          canonicalJob={canonicalJob ?? ''}
          width={runbookWidth}
          minWidth={MIN_RUNBOOK_WIDTH}
          maxWidth={typeof window !== 'undefined' ? window.innerWidth - MIN_MAIN_WIDTH : DEFAULT_RUNBOOK_WIDTH}
          onWidthChange={setRunbookWidth}
          onClose={() => setShowRunbook(false)}
        />
      )}
    </div>
  );
}

export function CanonicalJobDrawer({
  onClose,
  onQueueUpdated,
  queue,
}: {
  onClose: () => void;
  onQueueUpdated: () => void;
  queue: QueueOverview;
}) {
  const [dialogMode, setDialogMode] = useState<'redrive' | 'discard' | null>(null);
  const [showRunbook, setShowRunbook] = useState(false);
  const [runbookWidth, setRunbookWidth] = useState(DEFAULT_RUNBOOK_WIDTH);

  return (
    <>
      <div className='fixed inset-0 z-50 bg-black/30' onClick={onClose} />
      <aside
        aria-label={`${queue.canonicalJob} queue details`}
        className='fixed inset-y-0 right-0 z-[60] flex w-full max-w-5xl flex-col border-l border-neutral-200 bg-white shadow-2xl'
        role='dialog'
      >
        <div className='flex items-center justify-between border-b border-neutral-100 px-5 py-3'>
          <div>
            <p className='text-xs font-medium uppercase tracking-wide text-neutral-400'>Queue details</p>
            <h2 className='font-mediumbold text-lg text-neutral-900'>{queue.canonicalJob}</h2>
          </div>
          <button
            aria-label='Close queue details'
            className='rounded-md px-2 py-1 text-xl text-neutral-400 hover:bg-neutral-50 hover:text-neutral-700'
            onClick={onClose}
            type='button'
          >
            ×
          </button>
        </div>
        <div className='min-h-0 flex-1 overflow-y-auto p-5'>
          <CanonicalJobContent
            dialogMode={dialogMode}
            onDialogModeChange={setDialogMode}
            onOpenRunbook={() => setShowRunbook(true)}
            onRefresh={onQueueUpdated}
            queue={queue}
          />
        </div>
      </aside>
      {showRunbook && (
        <RunbookChat
          canonicalJob={queue.canonicalJob}
          width={runbookWidth}
          minWidth={MIN_RUNBOOK_WIDTH}
          maxWidth={typeof window !== 'undefined' ? window.innerWidth - MIN_MAIN_WIDTH : DEFAULT_RUNBOOK_WIDTH}
          onWidthChange={setRunbookWidth}
          onClose={() => setShowRunbook(false)}
        />
      )}
    </>
  );
}

function CanonicalJobContent({
  dialogMode,
  onDialogModeChange,
  onOpenRunbook,
  onRefresh,
  queue,
}: {
  dialogMode: 'redrive' | 'discard' | null;
  onDialogModeChange: (mode: 'redrive' | 'discard' | null) => void;
  onOpenRunbook: () => void;
  onRefresh: () => void;
  queue: QueueOverview;
}) {
  const { dlqLoading, dlqMessages, dlqUrl, refreshDlq, smartRedriveInfo } = useCanonicalJobDlq(queue.canonicalJob);
  const { clearJobNameFilters, dlqByJobName, filteredDlqMessages, jobNameFilters, toggleJobNameFilter } =
    useJobNameFilters(dlqMessages);
  const {
    clearSelection,
    expandedId,
    hasSelection,
    selectedIds,
    selectedMessages,
    toggleExpanded,
    toggleSelect,
    toggleSelectAll,
  } = useDlqSelection(filteredDlqMessages);

  function handleRefresh() {
    onRefresh();
    refreshDlq();
  }

  return (
    <div className='space-y-6'>
      <CanonicalJobHeader onOpenRunbook={onOpenRunbook} onRefresh={handleRefresh} queue={queue} />
      <CanonicalJobFilters
        clearJobNameFilters={clearJobNameFilters}
        dlqByJobName={dlqByJobName}
        jobNameFilters={jobNameFilters}
        jobNames={queue.jobNames}
        onToggleJobNameFilter={toggleJobNameFilter}
        onClearSelection={clearSelection}
      />
      <CanonicalJobDlqTable
        canonicalJob={queue.canonicalJob}
        dlqLoading={dlqLoading}
        expandedId={expandedId}
        hasSelection={hasSelection}
        messages={filteredDlqMessages}
        onDiscardSelected={() => onDialogModeChange('discard')}
        onRedriveSelected={() => onDialogModeChange('redrive')}
        onToggleExpanded={toggleExpanded}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        selectedIds={selectedIds}
      />

      {dialogMode != null && (
        <RedriveDialog
          mode={dialogMode}
          messages={selectedMessages}
          dlqUrl={dlqUrl}
          canonicalJob={queue.canonicalJob}
          smartRedriveInfo={smartRedriveInfo}
          onConfirm={() => {
            onDialogModeChange(null);
            clearSelection();
            refreshDlq();
          }}
          onCancel={() => onDialogModeChange(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CanonicalJobHeader({
  onOpenRunbook,
  onRefresh,
  queue,
}: {
  onOpenRunbook: () => void;
  onRefresh: () => void;
  queue: QueueOverview;
}) {
  return (
    <div className='rounded-lg border border-neutral-200 bg-white p-6'>
      <div className='flex items-start justify-between'>
        <div>
          <h2 className='font-mediumbold mb-1 text-2xl text-neutral-900'>{queue.canonicalJob}</h2>
          <div className='mt-3 flex gap-6 text-sm text-neutral-600'>
            <div>
              <span className='text-neutral-400'>Pod:</span> {queue.pod}
            </div>
            <div>
              <span className='text-neutral-400'>Main Queue:</span>{' '}
              <span className='tabular-nums'>{queue.mainQueueCount}</span>
            </div>
            <div>
              <span className='text-neutral-400'>DLQ:</span>{' '}
              <span className={`tabular-nums ${queue.dlqCount > 0 ? 'font-medium text-red-600' : ''}`}>
                {queue.dlqCount}
              </span>
            </div>
          </div>
        </div>
        <div className='flex gap-2'>
          <button
            onClick={onOpenRunbook}
            className='rounded-md border border-purple-300 bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-100'
          >
            Runbook
          </button>
          <button
            onClick={onRefresh}
            className='rounded-md border border-neutral-200 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50'
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

function CanonicalJobFilters({
  clearJobNameFilters,
  dlqByJobName,
  jobNameFilters,
  jobNames,
  onToggleJobNameFilter,
  onClearSelection,
}: {
  clearJobNameFilters: () => void;
  dlqByJobName: Array<[string, number]>;
  jobNameFilters: Set<string>;
  jobNames: string[];
  onToggleJobNameFilter: (name: string) => void;
  onClearSelection: () => void;
}) {
  return (
    <div className='rounded-lg border border-neutral-200 bg-white'>
      <div className='border-b border-neutral-200 px-6 py-3'>
        <h3 className='font-mediumbold text-sm text-neutral-900'>Job Names ({jobNames.length})</h3>
      </div>
      {jobNames.length === 0 ? (
        <div className='px-6 py-4 text-sm text-neutral-500'>No job names registered.</div>
      ) : (
        <div className='flex flex-wrap gap-2 px-6 py-4'>
          {jobNames.map((name) => {
            const dlqCount = dlqByJobName.find(([jobName]) => jobName === name)?.[1] ?? 0;
            const isActive = jobNameFilters.has(name);

            return (
              <button
                key={name}
                onClick={() => {
                  onToggleJobNameFilter(name);
                  onClearSelection();
                }}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                  isActive ? 'bg-neutral-700 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                <span className='font-mono'>{name}</span>
                {dlqCount > 0 && (
                  <span
                    className={`rounded-full px-1.5 py-0 text-[10px] ${
                      isActive ? 'bg-red-400/30 text-red-200' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {dlqCount} DLQ
                  </span>
                )}
                {isActive && <span className='ml-0.5'>×</span>}
              </button>
            );
          })}
          {jobNameFilters.size > 0 && (
            <button
              onClick={() => {
                clearJobNameFilters();
                onClearSelection();
              }}
              className='text-xs text-neutral-500 hover:text-neutral-700'
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CanonicalJobDlqTable({
  canonicalJob,
  dlqLoading,
  hasSelection,
  messages,
  expandedId,
  onDiscardSelected,
  onRedriveSelected,
  onToggleExpanded,
  onToggleSelect,
  onToggleSelectAll,
  selectedIds,
}: {
  canonicalJob: string;
  dlqLoading: boolean;
  hasSelection: boolean;
  messages: DlqMessageSummary[];
  expandedId: string | null;
  onDiscardSelected: () => void;
  onRedriveSelected: () => void;
  onToggleExpanded: (messageId: string) => void;
  onToggleSelect: (messageId: string) => void;
  onToggleSelectAll: () => void;
  selectedIds: Set<string>;
}) {
  return (
    <div className='rounded-lg border border-neutral-200 bg-white'>
      <div className='flex items-center justify-between border-b border-neutral-200 px-6 py-3'>
        <h3 className='font-mediumbold text-sm text-neutral-900'>
          {dlqLoading ? 'DLQ Messages' : `DLQ Messages (${messages.length})`}
        </h3>
        <div className='flex gap-2'>
          <button
            onClick={onRedriveSelected}
            disabled={!hasSelection}
            className='rounded-md border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50'
          >
            Redrive Selected ({selectedIds.size})
          </button>
          <button
            onClick={onDiscardSelected}
            disabled={!hasSelection}
            className='rounded-md border border-red-300 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50'
          >
            Discard Selected ({selectedIds.size})
          </button>
        </div>
      </div>
      {dlqLoading ? (
        <DataTableSkeleton columns={5} label='Loading DLQ messages' rows={4} />
      ) : messages.length === 0 ? (
        <div className='px-6 py-4 text-sm text-neutral-500'>No DLQ messages.</div>
      ) : (
        <div className='overflow-x-auto'>
          <table className='w-full text-left text-sm'>
            <thead className='border-b border-neutral-200 bg-neutral-50'>
              <tr>
                <th className='px-3 py-2.5'>
                  <input
                    type='checkbox'
                    checked={selectedIds.size === messages.length && messages.length > 0}
                    onChange={onToggleSelectAll}
                    className='rounded border-neutral-300'
                  />
                </th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Job Name</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Trace ID</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Sent</th>
                <th className='px-3 py-2.5 text-right font-medium text-neutral-700'>Receives</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Preview</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((message) => (
                <Fragment key={message.messageId}>
                  <tr
                    onClick={() => onToggleExpanded(message.messageId)}
                    className={`cursor-pointer border-b border-neutral-100 hover:bg-neutral-50 ${
                      expandedId === message.messageId ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className='px-3 py-2.5' onClick={(event) => event.stopPropagation()}>
                      <input
                        type='checkbox'
                        checked={selectedIds.has(message.messageId)}
                        onChange={() => onToggleSelect(message.messageId)}
                        className='rounded border-neutral-300'
                      />
                    </td>
                    <td className='px-3 py-2.5 font-mono text-xs text-neutral-800'>{message.jobName ?? '—'}</td>
                    <td className='px-3 py-2.5 font-mono text-xs text-neutral-600'>
                      {message.traceId ? (
                        <a
                          href={`/job-worker-traces?traceId=${encodeURIComponent(message.traceId)}&timeRange=259200000`}
                          onClick={(event) => event.stopPropagation()}
                          className='text-blue-600 hover:text-blue-800 hover:underline'
                        >
                          {truncate(message.traceId, 16)}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className='px-3 py-2.5 text-neutral-600'>{formatTimestamp(message.sentTimestamp)}</td>
                    <td className='px-3 py-2.5 text-right tabular-nums text-neutral-700'>{message.receiveCount}</td>
                    <td className='max-w-xs px-3 py-2.5 text-neutral-600'>
                      <span className='block truncate'>{truncate(message.body, 100)}</span>
                    </td>
                  </tr>
                  {expandedId === message.messageId && (
                    <tr className='border-b border-neutral-100 bg-neutral-50'>
                      <td colSpan={6} className='px-6 py-4'>
                        <div className='mb-3 flex items-center gap-3'>
                          <span className='text-xs font-medium text-neutral-500'>Message ID:</span>
                          <code className='text-xs text-neutral-700'>{message.messageId}</code>
                          {message.traceId &&
                            (() => {
                              const axiomUrl = buildAxiomUrl(message, canonicalJob);

                              return axiomUrl != null ? (
                                <a
                                  href={axiomUrl}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='ml-auto text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline'
                                >
                                  View in Axiom
                                </a>
                              ) : null;
                            })()}
                        </div>
                        <pre className='max-h-80 overflow-auto rounded-md bg-neutral-900 p-4 text-xs text-neutral-100'>
                          {formatJsonBody(message.body)}
                        </pre>
                        {Object.keys(message.messageAttributes).length > 0 && (
                          <div className='mt-3'>
                            <span className='text-xs font-medium text-neutral-500'>Message Attributes:</span>
                            <pre className='mt-1 rounded-md bg-neutral-100 p-2 text-xs text-neutral-700'>
                              {JSON.stringify(message.messageAttributes, null, 2)}
                            </pre>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
